import React from "react";
import "./App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import TarteauCitronLoader from "./components/TarteauCitronLoader";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import AuthVerifyEmailPage from "./pages/AuthVerifyEmailPage";
import AuthCallback from "./pages/AuthCallback";
import GoogleCallback from "./pages/GoogleCallback";
import FloorPlanPage from "./pages/FloorPlanPage";
import DashboardPage from "./pages/DashboardPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminPage from "./pages/AdminPage";
import CalendarPage from "./pages/CalendarPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import CGVPage from "./pages/CGVPage";
import ConfidentialitePage from "./pages/ConfidentialitePage";
import EventsPage from "./pages/EventsPage";
import PartnershipPage from "./pages/PartnershipPage";
import AddAddressPage from "./pages/partnership/AddAddressPage";
import CloneSitePage from "./pages/partnership/CloneSitePage";
import AffiliatePage from "./pages/partnership/AffiliatePage";
import RecurringBookingPage from "./pages/RecurringBookingPage";
import MyAccessPage from "./pages/MyAccessPage";
import QuickBookPage from "./pages/QuickBookPage";
import MobileHubPage from "./pages/MobileHubPage";
import PWAInstallPage from "./pages/PWAInstallPage";
import AdminMobilePage from "./pages/AdminMobilePage";
import usePageViewTracker from "./hooks/usePageViewTracker";

function AppRouter() {
  const location = useLocation();
  // Fire-and-forget analytics on every client-side navigation
  usePageViewTracker();

  // CRITICAL: Detect session_id in hash SYNCHRONOUSLY before any route renders
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide navbar/footer on auth, install and PWA hub pages (chromeless app feel)
  const hideChrome = ['/auth', '/auth/google', '/auth/callback', '/m', '/install', '/admin/m'].includes(location.pathname);

  return (
    <>
      {!hideChrome && <Navbar />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/verify" element={<AuthVerifyEmailPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google" element={<GoogleCallback />} />
        <Route path="/plan" element={<FloorPlanPage />} />
        <Route path="/calendrier" element={<CalendarPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/abonnements" element={<SubscriptionPage />} />
        <Route path="/tarifs" element={<PricingPage />} />
        <Route path="/evenements" element={<EventsPage />} />
        <Route path="/recurrente" element={<RecurringBookingPage />} />
        <Route path="/partenariat" element={<PartnershipPage />} />
        <Route path="/partenariat/ajouter-mon-adresse" element={<AddAddressPage />} />
        <Route path="/partenariat/cloner-le-site" element={<CloneSitePage />} />
        <Route path="/partenariat/affiliation" element={<AffiliatePage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/m" element={<MobileHubPage />} />
        <Route path="/install" element={<PWAInstallPage />} />
        <Route path="/mes-acces" element={<MyAccessPage />} />
        <Route path="/quick-book" element={<QuickBookPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/m" element={<AdminMobilePage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/cgv" element={<CGVPage />} />
        <Route path="/confidentialite" element={<ConfidentialitePage />} />
      </Routes>
      {!hideChrome && <Footer />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <SiteSettingsProvider>
            <AppRouter />
            <TarteauCitronLoader />
            <Toaster position="top-right" richColors />
          </SiteSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
